# AddAppointment.vue
<template lang="pug">
y-dialog(fill-height fluid v-model="showModal" scrollable transition="fade-transition" persistent)
  v-card
    v-card-title.px-4.h6.basic800--text {{ $t('schedule_demo') }}
    v-divider.basic300
    v-card-text.pb-0.px-4.pt-4
      v-form(ref="form")
        v-alert(
          v-if="error"
          type="error"
          text
          dismissible
          @input="error = null"
        ) {{ error }}

        v-menu(
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        )
          template(v-slot:activator="{ on, attrs }")
            v-text-field(
              v-model="appointmentData.date"
              :label="$t('date')"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              :rules="rules.not_empty"
            )
          v-date-picker(
            v-model="appointmentData.date"
            no-title
            @input="dateMenu = false"
          )
            
        v-menu(
          v-model="timeMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        )
          template(v-slot:activator="{ on, attrs }")
            v-text-field(
              v-model="appointmentData.time"
              :label="$t('time')"
              prepend-icon="mdi-clock-time-four-outline"
              readonly
              v-bind="attrs"
              v-on="on"
              :rules="rules.not_empty"
            )
          v-time-picker(
            v-model="appointmentData.time"
            format="24hr"
            @click:minute="timeMenu = false"
          )

        v-text-field(
          v-model="appointmentData.venue"
          :label="$t('venue')"
          prepend-icon="mdi-map-marker-outline"
        )

    v-card-actions
      v-spacer
      v-btn(color='white', text @click='showModal = false')
        .button-Aa-small.primary400--text {{ $t('cancel') }}
      v-btn.mr-2(
        color='textColorAccent'
        depressed
        height=32
        @click='clickUpdate'
        :loading="isUpdating"
        :disabled="!canSubmit"
        dark
      )
        .button-AA-small.basic100--text {{ $t('update') }}
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'AddAppointment',
  props: ['open', 'lead'],
  data: () => ({
    appointmentData: {
      date: '',
      time: '',
      venue: ''
    },
    dateMenu: false,
    timeMenu: false,
    error: null,
    isUpdating: false,
    rules: {
      not_empty: [val => (val || '').length > 0 || 'This field is required']
    }
  }),
  computed: {
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    canSubmit() {
      return this.appointmentData.date && this.appointmentData.time && !this.error && this.$refs.form.validate()
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.initializeData()
      }
    }
  },
  mounted() {
    this.initializeData()
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    initializeData() {
      if (this.lead.extra_fields_data) {
        const fields = this.lead.extra_fields_data
        this.appointmentData = {
          date: fields.find(f => f.name === 'Appointment Date')?.value || '',
          time: fields.find(f => f.name === 'Appointment Time')?.value || '',
          venue: fields.find(f => f.name === 'Appointment Venue')?.value || ''
        }
      }
    },
    clickUpdate() {
      if (!this.$refs.form.validate()) return

      this.isUpdating = true

      const appointmentStartDate = new Date(`${this.appointmentData.date} ${this.appointmentData.time}`)
      const offset = appointmentStartDate.getTimezoneOffset()
      const appointmentStartISO = new Date(appointmentStartDate.getTime() - (offset*60000)).toISOString().replace('Z', '+08:00')
      const appointmentEndISO = new Date(appointmentStartDate.getTime() - (offset*60000) + 60*60000).toISOString().replace('Z', '+08:00')

      const fields = [
        {
          type: 'text',
          name: 'Appointment Date',
          enabled: true,
          value: this.appointmentData.date
        },
        {
          type: 'text',
          name: 'Appointment Time',
          enabled: true,
          value: this.appointmentData.time
        },
        {
          type: 'text',
          name: 'Appointment Venue',
          enabled: true,
          value: this.appointmentData.venue
        }
      ]

      const extra_fields_data = Array.isArray(this.lead?.extra_fields_data) 
        ? [...this.lead.extra_fields_data]
        : []

      fields.forEach(field => {
        const existingIndex = extra_fields_data.findIndex(f => f.name === field.name)
        if (existingIndex >= 0) {
          extra_fields_data[existingIndex] = { ...extra_fields_data[existingIndex], ...field }
        } else {
          extra_fields_data.push(field)
        }
      })

      const isReschedule = extra_fields_data.some(f => f.name === 'Appointment Date' && f.value)
//https://yezza.app.n8n.cloud/webhook-test/d640e294-cbf3-4200-b529-ce9db58fb79c
      this.$axios.patch(`/leads/${this.lead.pid}/`, { 
        status: 'request',
        extra_fields_data 
      })
        .then(async resp => {
          // Add n8n webhook here
          await this.$axios.post('https://flow.yezza.com/webhook/d640e294-cbf3-4200-b529-ce9db58fb79c', {
            leadId: this.lead.pid,
            customerName: this.lead.name,
            staff: this.lead.member.id,
            customerEmail: this.lead.email,
            customerPhone: this.lead.phone,
            appointmentStartDate: appointmentStartISO,
            appointmentEndDate: appointmentEndISO,
            venue: this.appointmentData.venue,
            isReschedule
          })

          this.$emit('update:lead', resp.data)
          this.showModal = false
          this.showMessage(['success', this.$t(isReschedule ? 'success_message.appointment_rescheduled' : 'success_message.appointment_scheduled')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_schedule')])
        })
        .finally(() => {
          this.isUpdating = false
        })
    }
  }
}
</script>

<i18n>
{
  "en": {
    "schedule_demo": "Schedule Demo",
    "date": "Date",
    "time": "Time", 
    "venue": "Venue (Optional)",
    "success_message": {
      "appointment_scheduled": "Appointment scheduled successfully",
      "appointment_rescheduled": "Appointment rescheduled successfully"
    },
    "error_message": {
      "unable_to_schedule": "Unable to schedule appointment. Please try again."
    }
  }
}
</i18n>